import videojs from 'video.js'
import { registerPluginOnce } from '../utils.js'

const Plugin = videojs.getPlugin('plugin')

// Default options for the plugin.
const defaults = {
  autofocus: false,
  seekDuration: 5
}

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBHotKeys extends Plugin {
  /**
   * Create a NFBHotKeys plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())

    this.options = videojs.obj.merge(defaults, options)
    this.keyDown = this.keyDown.bind(this)

    this.player.ready(() => {
      this.init()
    })
  }

  init () {
    this.log('Initializing NFB Hot Keys plugin')

    // hotkey work only if player is focused
    this.player.on('keydown', this.keyDown)

    if (this.options.autofocus) {
      this.player.focus()
    }
  }

  keyDown (e) {
    const evt = e || window.event
    const charCode = evt.keyCode || evt.which

    // hot keys only works if controls enabled
    if (this.player.controls()) {
      const activeEl = document.activeElement
      if (activeEl === this.player.el() ||
          activeEl === this.player.el().querySelector('.vjs-tech') ||
          activeEl === this.player.el().querySelector('.vjs-control-bar') ||
          activeEl === this.player.el().querySelector('.iframeblocker')) {
        this.log(`Key down: ${charCode}`)
        this.applyHotkeyAction(charCode, evt.preventDefault.bind(evt))
      }
    }
  }

  applyHotkeyAction (keyId, ePreventDefault) {
    let wasPlaying, seekTime
    const duration = this.player.duration()
    ePreventDefault()

    switch (keyId) {
    // Press spacebar key to play/pause
    case 32:
      this.log('Spacebar is pressed')
      if (this.player.paused()) {
        this.silencePromise(this.player.play())
      } else {
        this.player.pause()
      }
      break
      // Press F key to fullscreen/exit fullscreen
    case 70:
      this.log('F key is pressed')
      if (this.player.isFullscreen()) {
        this.player.exitFullscreen()
      } else {
        this.player.requestFullscreen()
      }
      break
      // Press M key to muted/unmuted
    case 77:
      this.log('M button is pressed')
      this.player.muted(!this.player.muted())
      break
      // Press left arrow key to seek backward
    case 37:
      this.log('Left arrow button is pressed')
      wasPlaying = !this.player.paused()
      if (wasPlaying) {
        this.player.pause()
      }
      seekTime = this.player.currentTime() - this.options.seekDuration
      // cant seek to negative time
      if (seekTime <= 0) {
        seekTime = 0
      }
      this.player.currentTime(seekTime)
      if (wasPlaying) {
        this.silencePromise(this.player.play())
      }
      break
      // Press right arrow key to seek forward
    case 39:
      this.log('Right arrow is pressed')
      wasPlaying = !this.player.paused()
      if (wasPlaying) {
        this.player.pause()
      }
      seekTime = this.player.currentTime() + this.options.seekDuration
      // cant seek to negative time
      if (seekTime >= duration) {
        seekTime = wasPlaying ? duration - 0.001 : duration
      }
      this.player.currentTime(seekTime)
      if (wasPlaying) {
        this.silencePromise(this.player.play())
      }
      break
      // Press top arrow key to increased volume
    case 38:
      this.log('Top arrow is pressed')
      this.player.volume(this.player.volume() + 0.1)
      break
      // Press bottom arrow key to decreased volume
    case 40:
      this.log('Bottom arrow is pressed')
      this.player.volume(this.player.volume() - 0.1)
      break
    }
  }

  silencePromise (value) {
    if (value != null && typeof value.then === 'function') {
      value.then(null, function (e) {})
    }
  }
}

// Define default values for the plugin's `state` object here.
NFBHotKeys.defaultState = {}

// Include the version number.
NFBHotKeys.VERSION = '1.0.0'

registerPluginOnce(videojs, 'nfbHotKeys', NFBHotKeys)

export default NFBHotKeys
